// @flow
import { createSelector } from 'reselect';
import { List, OrderedMap, Map as ImmutableMap } from 'immutable';
import moment from 'moment/moment';

import { getAccountsById, getShowGoalAccounts } from 'data/accounts/selectors';
import { getAllBalancesByAccountId, getIncludeGoalTxnsInBalances } from 'data/accountBalances/selectors';

import { isAcme, isQuicken } from 'isAcme';

import { AccountNode } from './types';

const isSeparateAccount = (account) => (isQuicken && account.isExcludedFromBudgets && account.isExcludedFromReports && account.type !== 'INVESTMENT') ||
  (isQuicken && account.isExcludedFromReports && account.type === 'INVESTMENT');

function getL1GroupingData(account) {
  if (isSeparateAccount(account)) {
    return {
      id: 'SEPARATE',
      displayLabel: 'Separate',
      displayOrder: 6,
      svgIconId: 'IconBanking',
      routeTo: '/transactions',
    };
  }

  switch (account.type) {
    case 'BANK':
    case 'CASH':
    case 'CREDIT':
      return {
        id: 'BANKING',
        displayLabel: 'Banking',
        displayOrder: 1,
        svgIconId: 'IconBanking',
        routeTo: '/transactions',
      };
    case 'INVESTMENT':
      return {
        id: 'INVESTMENT',
        displayLabel: 'Investments',
        displayOrder: 2,
        svgIconId: 'IconInvestment',
        routeTo: isAcme ? '/investing' : '/investments',
      };
    case 'LOAN':
    case 'OTHER_ASSET':
    case 'OTHER_LIABILITY':
    case 'REAL_ESTATE':
    case 'VEHICLE':
      return {
        id: 'ASSETS_LIABILITIES',
        displayLabel: 'Property & Debt',
        displayOrder: 3,
        svgIconId: 'IconLoan',
        routeTo: '/transactions',
      };
    case 'GOAL':
      return {
        id: 'GOALS',
        displayLabel: 'Savings Goals',
        displayOrder: 4,
        svgIconId: 'IconLoan',
        routeTo: '/goals',
      };
    default:
      return {
        id: 'OTHER',
        displayLabel: 'Other',
        displayOrder: 5,
        svgIconId: 'IconBanking',
        routeTo: '/transactions',
      };
  }
}

function hasL2Grouping(id, numberOfAccounts) {
  // log.log('Checking if node has L2 grouping', id, numberOfAccounts);
  switch (id) {
    case 'BANKING':
      return numberOfAccounts > 0;
    case 'INVESTMENT':
      return numberOfAccounts > 0;
    case 'ASSETS_LIABILITIES':
      return numberOfAccounts > 0;
    default:
      return false;
  }
}

function getL2GroupingData(node, account) {

  if (account.isExcludedFromAccountBar && account.type !== 'INVESTMENT') {
    return {
      id: 'HIDDEN',
      displayLabel: 'Hidden',
      displayOrder: 4,
      routeTo: node.routeTo,
    };
  }

  switch (account.type) {
    case 'BANK':
    case 'CASH':
    case 'CREDIT':
      if (account.type === 'CASH' || account.subType === 'CHECKING') {
        return {
          id: 'CASH_CHECKING',
          displayLabel: 'Cash & Checking',
          displayOrder: 1,
          routeTo: node.routeTo,
        };
      }
      if (account.type === 'CREDIT') {
        return {
          id: 'CREDIT',
          displayLabel: 'Credit',
          displayOrder: 2,
          routeTo: node.routeTo,
        };
      }
      if (account.subType === 'SAVINGS') {
        return {
          id: 'SAVINGS',
          displayLabel: 'Savings',
          displayOrder: 3,
          routeTo: node.routeTo,
        };
      }
      return {
        id: 'OTHER_BANKING',
        displayLabel: 'Other Banking',
        displayOrder: 4,
        routeTo: node.routeTo,
      };
    case 'INVESTMENT':
      switch (account.subType) {
        case 'BROKERAGE':
          return {
            id: 'BROKERAGE',
            displayLabel: 'Brokerage',
            displayOrder: 1,
            routeTo: node.routeTo,
          };
        case 'IRA':
        case 'KEOGH':
        case 'SEP_IRA':
        case 'SIMPLE_IRA':
        case '401K':
        case '403B':
          return {
            id: 'RETIREMENT',
            displayLabel: 'Retirement',
            displayOrder: 2,
            routeTo: node.routeTo,
          };
        case 'UGMA_UTMA':
        case '529':
        case 'OTHER_INVESTMENT':
        default:
          return {
            id: 'OTHER_INVESTMENT',
            displayLabel: 'Other Investments',
            displayOrder: 3,
            routeTo: node.routeTo,
          };
      }
    case 'OTHER_ASSET':
    case 'REAL_ESTATE':
    case 'VEHICLE':
      return {
        id: 'PROPERTY',
        displayLabel: 'Property',
        displayOrder: 1,
        routeTo: node.routeTo,
      };
    case 'LOAN':
    case 'OTHER_LIABILITY':
      return {
        id: 'DEBT',
        displayLabel: 'Debt',
        displayOrder: 1,
        routeTo: node.routeTo,
      };
    default:
      return {
        id: 'OTHER',
        displayLabel: 'Other',
        displayOrder: 1,
        routeTo: node.routeTo,
      };
  }
}

const findOrCreateNodeForGroup = (list, data, depth) => {
  let foundNode = list.find((node) => node.id === data.id);
  if (!foundNode) {
    list.push(foundNode = { ...data, depth, children: [] });
  }
  return foundNode;
};

const rollupAccountNodes = (node, balancesByAccountId, doIncludeGoalTxnsInBalances) => { /* eslint-disable no-param-reassign */
  let allAccountIds = [];
  let sumEndingAccountBalances = 0.0;
  let sumOnlineAccountBalances = 0.0;
  let sumCurrentAccountBalances = 0.0;
  let sumFinalAccountBalances = 0.0;
  let sumGoalsAccountBalances = 0.0;
  let earliestTransactionDate = null;
  let currency = null;

  if ((!doIncludeGoalTxnsInBalances || node.id !== 'GOALS') && node.children) {
    node.children = node.children.map((child) => {
      const childAfterRollup = rollupAccountNodes(child, balancesByAccountId, doIncludeGoalTxnsInBalances);
      allAccountIds = allAccountIds.concat(childAfterRollup.allAccountIds.toArray());
      sumEndingAccountBalances += childAfterRollup.sumEndingAccountBalances;
      sumOnlineAccountBalances += childAfterRollup.sumOnlineAccountBalances;
      sumCurrentAccountBalances += childAfterRollup.sumCurrentAccountBalances;
      sumFinalAccountBalances += childAfterRollup.sumFinalAccountBalances;
      sumGoalsAccountBalances += childAfterRollup.sumGoalsAccountBalances;

      if (!earliestTransactionDate) {
        earliestTransactionDate = childAfterRollup.earliestTransactionDate;
      } else if (childAfterRollup.earliestTransactionDate && moment(childAfterRollup.earliestTransactionDate).isBefore(earliestTransactionDate)) {
        earliestTransactionDate = moment(childAfterRollup.earliestTransactionDate);
      }

      if (!currency) {
        currency = childAfterRollup.currency;
      } else if (currency !== childAfterRollup.currency) {
        currency = 'N/A';
      }

      return childAfterRollup;
    });
    node.children = List(node.children);
  }

  if (node.account) {
    allAccountIds.push(node.account.id);
    const balancesForAccount = balancesByAccountId.get(node.account.id);
    sumEndingAccountBalances += (balancesForAccount ? balancesForAccount.endingBalance : 0.0);
    sumOnlineAccountBalances += (balancesForAccount ? balancesForAccount.onlineBalance : 0.0);
    sumCurrentAccountBalances += (balancesForAccount ? balancesForAccount.currentBalance : 0.0);
    sumFinalAccountBalances += (balancesForAccount ? balancesForAccount.finalBalance : 0.0);
    sumGoalsAccountBalances += (balancesForAccount ? balancesForAccount.goalsBalance : 0.0);

    if (!earliestTransactionDate) {
      earliestTransactionDate = balancesForAccount.earliestTransactionDate;
    } else if (balancesForAccount.earliestTransactionDate && moment(balancesForAccount.earliestTransactionDate).isBefore(earliestTransactionDate)) {
      earliestTransactionDate = moment(balancesForAccount.earliestTransactionDate);
    }

    if (!currency) {
      currency = node.account.currency;
    } else if (currency !== node.account.currency) {
      currency = 'N/A';
    }
  }

  node.allAccountIds = memoizedAccountIdList(allAccountIds);
  node.sumEndingAccountBalances = sumEndingAccountBalances;
  node.sumOnlineAccountBalances = sumOnlineAccountBalances;
  node.sumCurrentAccountBalances = sumCurrentAccountBalances;
  node.sumFinalAccountBalances = sumFinalAccountBalances;
  node.sumGoalsAccountBalances = sumGoalsAccountBalances;
  node.currency = currency;
  node.earliestTransactionDate = earliestTransactionDate;
  node.hide = node.account ? (node.account.isExcludedFromAccountBar && isAcme) : false;

  return new AccountNode(node);
};

const accountIdsCache = {};

function memoizedAccountIdList(ids) {

  const normalizedIds = ids.sort((a, b) => a > b);
  const key = JSON.stringify(normalizedIds);
  if (!accountIdsCache[key]) {
    accountIdsCache[key] = List(ids);
  }
  return accountIdsCache[key];

}


function hasHiddenChild(node) {
  return node.children && node.children.find((x) => x.account.isExcludedFromAccountBar);
}

export const getAccountNodesTree = createSelector(
  getAccountsById,
  getAllBalancesByAccountId,
  getShowGoalAccounts,
  getIncludeGoalTxnsInBalances,
  (accountsById, balancesByAccountId, doShowGoalAccounts, doIncludeGoalTxnsInBalances) => {

    // reduce all accounts into L1 groupings
    let l1Nodes = accountsById.reduce((list, account) => {
      if (doShowGoalAccounts || account.type !== 'GOAL') {
        const allNodes = findOrCreateNodeForGroup(list, {
          id: 'all',
          displayLabel: isAcme? 'Net Worth' : 'Total',
          routeTo: '/transactions',
          displayOrder: 0,
          expandable: false,
        }, 1);

        if(!isSeparateAccount(account)){
          allNodes.children.push({
            id: account.id,
            depth: 2,
            displayLabel: account.name,
            routeTo: '/transactions',
            account,
          });
        }

        const l1Group = findOrCreateNodeForGroup(list, getL1GroupingData(account), 1);
        l1Group.children.push({ id: account.id, depth: 2, displayLabel: account.name, routeTo: l1Group.routeTo, account });
      }
      return list;
    }, []);

    // after creating all l1Nodes, sort by display order
    l1Nodes.sort((a, b) => a.displayOrder - b.displayOrder);

    // expand children in L1 groupings into L2 groupings (if required) and perform sorting
    l1Nodes.forEach((node) => {
      if (node.id !== 'all') {
        if (hasL2Grouping(node.id, node.children.length) || hasHiddenChild(node)) {
          node.children = node.children.reduce((list, child) => {

            const l2Group = findOrCreateNodeForGroup(list, getL2GroupingData(node, child.account), 2);
            l2Group.children.push(
              { id: child.account.id, depth: 3, displayLabel: child.account.name, routeTo: l2Group.routeTo, account: child.account }
            );

            return list;
          }, []);
          node.children.sort((a, b) => a.displayOrder - b.displayOrder);
          node.children.forEach((l3Node) => {
            l3Node.children.sort((a, b) => a.displayLabel.localeCompare(
              b.displayLabel,
              undefined,
              { numeric: true, sensitivity: 'base' }
            ));
          });
        } else {
          node.children.sort((a, b) => a.displayLabel.localeCompare(
            b.displayLabel,
            undefined,
            { numeric: true, sensitivity: 'base' }
          ));
          node.children.forEach((l3Node) => {
            l3Node.isLeaf = true;
          });
        }
      } else {
        node.children.sort((a, b) => (
          a.displayLabel.localeCompare(
            b.displayLabel,
            undefined,
            { numeric: true, sensitivity: 'base' }
          )
        ));
      }
    });

    l1Nodes = l1Nodes.map((node) => rollupAccountNodes(node, balancesByAccountId, doIncludeGoalTxnsInBalances));
    return List(l1Nodes);
  }
);

const getAccountsByIdForNode = (node) => {
  let flattenedList = [[node.id, node]];
  if (node.children) {
    flattenedList = node.children.reduce((list, child) => (
      list.concat(getAccountsByIdForNode(child))
    ), flattenedList);
  }
  return flattenedList;
};

export const getAccountNodesById = createSelector(
  getAccountNodesTree,
  (tree) => {
    if (!tree || tree.size === 0) {
      return OrderedMap();
    }
    return OrderedMap(tree.reduce((list, node) => (
      list.concat(getAccountsByIdForNode(node))
    ), []));
  }
);

export const getBankAccounts = createSelector(
  getAccountNodesTree,
  getAccountsById,
  (allNodes, allAccounts) => {
    const bankNodes = new Map();
    allNodes
      .forEach((node) => {
        node.allAccountIds
          .forEach((id) => {
            const childAcct = allAccounts.get(id);
            if (childAcct?.type === 'BANK' || childAcct.subType === 'SAVINGS') bankNodes.set(id, childAcct);
          });
      });
    return ImmutableMap(bankNodes);
  }
);


